import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { fromPromise } from '@apollo/client/link/utils';

import { defaultLocalStorageAdapter } from 'src/shared/graphql';
import type { Jwt } from 'src/shared/graphql/generated';
import { mutations } from 'src/shared/graphql/requests';
import { isBrowser } from 'src/shared/helpers/environment';
import { ChannelEnum, openChannel } from 'src/shared/helpers/webviewChannel';

import { isAuthorizationError } from '../../errors';

import { link as queueLink } from './queueLink';

interface RefreshLinkOptions {
  getClient: () => ApolloClient<NormalizedCacheObject>;
  onUnauthenticated?: (skipRedirect: boolean) => void;
}

const missingRefreshToken = new Error('Missing refreshToken');
const missingJwtError = new Error('Missing Jwt');

const getNewToken = async (
  client: ReturnType<RefreshLinkOptions['getClient']>,
) => {
  const refreshToken = await defaultLocalStorageAdapter.getRefreshToken();
  if (refreshToken === null) {
    throw missingRefreshToken;
  }
  const refreshTokenResponse = await mutations.refreshToken(client, {
    refreshToken,
  });

  const jwt = refreshTokenResponse.data?.refreshJwt;
  if (!jwt) {
    throw missingJwtError;
  }

  return jwt;
};

const getNewTokenViaApp = async () => {
  const accessToken = await defaultLocalStorageAdapter.getAccessToken();
  const channel = openChannel<any, Jwt>(ChannelEnum.SHARE_HOVER_AUTHORIZATION);

  channel.send({ expired: accessToken });

  return channel.receive;
};

const updateToken = async (jwt: Jwt) => {
  const { accessToken, refreshToken } = jwt;

  await defaultLocalStorageAdapter.setAccessToken(accessToken);
  if (refreshToken) {
    await defaultLocalStorageAdapter.setRefreshToken(refreshToken);
  }

  return jwt;
};

let intervalId: NodeJS.Timeout;

const createRefreshLink = ({
  onUnauthenticated,
  getClient,
}: RefreshLinkOptions) => {
  let tokenRequest: Promise<Jwt> | null = null;

  const refreshToken = async (skipRedirect: boolean) => {
    queueLink.close();

    try {
      // Using the `.catch` to create a promise which
      // when awaited on the next line will trigger
      // an exception if it throws.
      tokenRequest = getNewTokenViaApp().catch(() => getNewToken(getClient()));

      return await updateToken(await tokenRequest);
    } catch (error) {
      onUnauthenticated?.(skipRedirect);
      return null;
    } finally {
      tokenRequest = null;
      queueLink.open();
    }
  };

  // Set up an interval to refresh the token
  // every 15 minutes
  if (isBrowser()) {
    clearInterval(intervalId);
    intervalId = setInterval(() => {
      void refreshToken(true);
    }, 1_000 * 60 * 15);
  }

  return onError(({ graphQLErrors, operation, forward }) => {
    const context = operation.getContext();
    const skipRedirect = !!context.skipRedirect;

    if (graphQLErrors && graphQLErrors.find(isAuthorizationError)) {
      return fromPromise(tokenRequest || refreshToken(skipRedirect)).flatMap(
        () => forward(operation),
      );
    }

    return undefined;
  });
};

export { createRefreshLink, RefreshLinkOptions };
